import React, { ReactNode } from 'react';

export interface TableColumn<T = any> {
  label: string;
  key: keyof T;
  render?: (value: T[keyof T], row: T) => ReactNode;
  sx?: Record<string, any>;
}

export interface TableAction<T = any> {
  label: string;
  onClick: (row: T) => void | Promise<void>;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  variant?: 'text' | 'outlined' | 'contained';
  startIcon?: React.ReactElement;
  condition?: (row: T) => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface SimpleConfig<TData, TCreateData = unknown, TUpdateData = unknown> {
  hooks: {
    useQuery: () => any;
    useCreate?: () => any;
    useUpdate?: () => any;
    useDelete?: () => any;
  };

  table: {
    title: string;
    columns: TableColumn<TData>[];
    emptyMessage?: string;
    errorMessage?: string;
  };

  actions?: {
    create?: {
      label: string;
      dialog: React.ComponentType<{
        open: boolean;
        onClose: () => void;
        editingItem?: TData | null;
        onSuccess?: () => void;
      }>;
    };
    edit?: {
      label: string;
    };
    delete?: {
      label: string;
      confirmTitle: string;
      confirmMessage: (item: TData) => string;
      transformParams?: (item: TData) => any;
    };
    custom?: TableAction<TData>[];
  };

  features?: {
    search?: boolean;
    pagination?: boolean;
    serverSidePagination?: boolean;
    onRowClick?: (row: TData) => void;
  };
}

export function extractHookData(hookResult: any) {
  return {
    data: hookResult.data || hookResult.items || [],
    isLoading: hookResult.isLoading || hookResult.loading || false,
    error: hookResult.error || null,
    refetch: hookResult.refetch,
    isFetching: hookResult.isFetching || false,
    // Server-side pagination fields
    totalCount: hookResult.totalCount || 0,
    pagination: hookResult.pagination || null,
  };
}

export function extractMutationData(mutationResult: any) {
  if (!mutationResult) return null;

  return {
    mutateAsync: mutationResult.mutateAsync,
    isPending: mutationResult.isPending || mutationResult.isLoading || false,
    mutate: mutationResult.mutate,
    isError: mutationResult.isError || false,
    error: mutationResult.error,
  };
}

export type DataManagementConfig<TData, TCreateData = any, TUpdateData = any> = SimpleConfig<
  TData,
  TCreateData,
  TUpdateData
>;
